import {
  faArrowDown,
  faArrowLeft,
  faArrowPointer,
  faArrowRight,
  faArrowUp,
  faEnvelope,
  faFile,
  faHandPointer,
  faLink,
  faMobileScreenButton,
  faPhone,
} from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';

import { readBoolean, readString } from '@/lib/prop-utils';
import { cmsPagesPreviewPath } from '@/lib/routes';
import type {
  ButtonConfig,
  ButtonIconName,
  ButtonIconPlacement,
  ButtonLinkType,
} from '@/types/button';

type ButtonLinkTypeOption = {
  value: ButtonLinkType;
  label: string;
  icon: IconDefinition;
  placeholder: string;
};

type ButtonIconOption = {
  value: ButtonIconName;
  label: string;
  icon: IconDefinition;
};

export const BUTTON_LINK_TYPE_OPTIONS: ButtonLinkTypeOption[] = [
  {
    value: 'page',
    label: 'Page',
    icon: faFile,
    placeholder: 'Select page',
  },
  {
    value: 'external',
    label: 'External URL',
    icon: faLink,
    placeholder: 'https://example.com',
  },
  {
    value: 'phone',
    label: 'Phone Number',
    icon: faPhone,
    placeholder: '(555) 555-5555',
  },
  {
    value: 'email',
    label: 'Email',
    icon: faEnvelope,
    placeholder: 'hello@example.com',
  },
];

export const BUTTON_ICON_OPTIONS: ButtonIconOption[] = [
  { value: 'phone', label: 'Phone', icon: faPhone },
  { value: 'mobile', label: 'Mobile', icon: faMobileScreenButton },
  { value: 'arrow-left', label: 'Arrow left', icon: faArrowLeft },
  { value: 'arrow-right', label: 'Arrow right', icon: faArrowRight },
  { value: 'arrow-up', label: 'Arrow up', icon: faArrowUp },
  { value: 'arrow-down', label: 'Arrow down', icon: faArrowDown },
  { value: 'external-link', label: 'External link', icon: faLink },
  { value: 'email', label: 'Email', icon: faEnvelope },
  { value: 'hand-pointer', label: 'Hand pointer', icon: faHandPointer },
  { value: 'arrow-pointer', label: 'Arrow pointer', icon: faArrowPointer },
];

const BUTTON_LINK_TYPE_SET = new Set<ButtonLinkType>(
  BUTTON_LINK_TYPE_OPTIONS.map((option) => option.value),
);

const BUTTON_ICON_SET = new Set<ButtonIconName>(BUTTON_ICON_OPTIONS.map((option) => option.value));
const BUTTON_ICON_PLACEMENT_SET = new Set<ButtonIconPlacement>(['before', 'after']);

const BUTTON_LINK_TYPE_PLACEHOLDER_MAP = new Map<ButtonLinkType, string>(
  BUTTON_LINK_TYPE_OPTIONS.map((option) => [option.value, option.placeholder]),
);

const BUTTON_ICON_MAP = new Map<ButtonIconName, IconDefinition>(
  BUTTON_ICON_OPTIONS.map((option) => [option.value, option.icon]),
);

function normalizeButtonLinkType(
  linkType: string | undefined,
  internalPageId?: string,
  link?: string,
): ButtonLinkType {
  if (linkType && BUTTON_LINK_TYPE_SET.has(linkType as ButtonLinkType)) {
    return linkType as ButtonLinkType;
  }

  if (internalPageId) {
    return 'page';
  }

  const inferredLinkType = inferLinkTypeFromLink(link);
  if (inferredLinkType) {
    return inferredLinkType;
  }

  return 'external';
}

function normalizeButtonStyle(
  style?: string,
  fallback: ButtonConfig['style'] = 'primary',
): ButtonConfig['style'] {
  if (style === 'primary' || style === 'secondary') {
    return style;
  }

  return fallback;
}

function normalizeButtonIconPlacement(
  iconPlacement?: string,
  fallback: ButtonIconPlacement = 'before',
): ButtonIconPlacement {
  if (iconPlacement && BUTTON_ICON_PLACEMENT_SET.has(iconPlacement as ButtonIconPlacement)) {
    return iconPlacement as ButtonIconPlacement;
  }

  return fallback;
}

function normalizeButtonIcon(icon?: string): ButtonIconName | undefined {
  if (!icon || !BUTTON_ICON_SET.has(icon as ButtonIconName)) {
    return undefined;
  }

  return icon as ButtonIconName;
}

function normalizeInitialLink(linkType: ButtonLinkType, link?: string): string {
  if (!link) {
    return '';
  }

  if (linkType === 'phone') {
    return link.replace(/^tel:/i, '');
  }

  if (linkType === 'email') {
    return link.replace(/^mailto:/i, '');
  }

  return link;
}

function normalizeExternalUrl(link?: string): string {
  const value = link?.trim() || '';
  if (!value) {
    return '';
  }

  const lowerValue = value.toLowerCase();
  if (value.startsWith('//')) {
    return `https:${value}`;
  }

  if (
    lowerValue.startsWith('http://') ||
    lowerValue.startsWith('https://') ||
    value.startsWith('/') ||
    value.startsWith('#')
  ) {
    return value;
  }

  return `https://${value}`;
}

function normalizePhoneValue(link?: string): string {
  return (link?.trim() || '').replace(/^tel:/i, '');
}

function normalizeEmailValue(link?: string): string {
  return (link?.trim() || '').replace(/^mailto:/i, '');
}

function inferLinkTypeFromLink(link?: string): ButtonLinkType | undefined {
  const trimmedLink = link?.trim() || '';

  if (/^tel:/i.test(trimmedLink)) {
    return 'phone';
  }

  if (/^mailto:/i.test(trimmedLink)) {
    return 'email';
  }

  return undefined;
}

function buildNormalizedButtonConfig({
  text,
  style,
  icon,
  iconPlacement,
  linkType,
  link,
  internalPageId,
  openInNewTab,
  nofollow,
  resolvedLink,
  defaultStyle,
  defaultIconPlacement,
}: {
  text?: string;
  style?: string;
  icon?: string;
  iconPlacement?: string;
  linkType?: string;
  link?: string;
  internalPageId?: string;
  openInNewTab?: boolean;
  nofollow?: boolean;
  resolvedLink?: string;
  defaultStyle?: ButtonConfig['style'];
  defaultIconPlacement?: ButtonIconPlacement;
}): ButtonConfig {
  const normalizedLinkType = normalizeButtonLinkType(linkType, internalPageId, link);

  return {
    text: text?.trim() || 'Button',
    style: normalizeButtonStyle(style, defaultStyle),
    icon: normalizeButtonIcon(icon),
    iconPlacement: normalizeButtonIconPlacement(iconPlacement, defaultIconPlacement),
    linkType: normalizedLinkType,
    link: normalizeInitialLink(normalizedLinkType, link),
    internalPageId,
    openInNewTab: openInNewTab === true,
    nofollow: nofollow === true,
    resolvedLink,
  };
}

function sanitizeButtonConfig(button: ButtonConfig): ButtonConfig {
  const linkType = normalizeButtonLinkType(button.linkType, button.internalPageId, button.link);
  const sanitized: ButtonConfig = {
    text: button.text.trim(),
    style: normalizeButtonStyle(button.style),
    linkType,
  };

  if (button.icon && BUTTON_ICON_SET.has(button.icon)) {
    sanitized.icon = button.icon;
    sanitized.iconPlacement = normalizeButtonIconPlacement(button.iconPlacement);
  } else {
    delete sanitized.iconPlacement;
  }

  if (linkType === 'page') {
    sanitized.internalPageId = button.internalPageId;
    return sanitized;
  }

  if (linkType === 'phone') {
    sanitized.link = normalizePhoneValue(button.link);
    return sanitized;
  }

  if (linkType === 'email') {
    sanitized.link = normalizeEmailValue(button.link);
    return sanitized;
  }

  sanitized.link = normalizeExternalUrl(button.link);
  sanitized.openInNewTab = button.openInNewTab === true;
  sanitized.nofollow = button.nofollow === true;

  return sanitized;
}

export function normalizeButtonConfig(
  button: Partial<ButtonConfig> | undefined,
  defaults?: Partial<Pick<ButtonConfig, 'style' | 'iconPlacement'>>,
): ButtonConfig {
  return buildNormalizedButtonConfig({
    text: button?.text,
    style: button?.style,
    icon: button?.icon,
    iconPlacement: button?.iconPlacement,
    linkType: button?.linkType,
    link: button?.link,
    internalPageId: button?.internalPageId,
    openInNewTab: button?.openInNewTab,
    nofollow: button?.nofollow,
    resolvedLink: button?.resolvedLink,
    defaultStyle: defaults?.style,
    defaultIconPlacement: defaults?.iconPlacement,
  });
}

export function readButtonConfig(
  props: Record<string, unknown> | undefined,
  defaults?: Partial<Pick<ButtonConfig, 'style' | 'iconPlacement'>>,
): ButtonConfig {
  return buildNormalizedButtonConfig({
    text: readString(props?.text),
    style: readString(props?.style),
    icon: readString(props?.icon),
    iconPlacement: readString(props?.iconPlacement),
    linkType: readString(props?.linkType),
    link: readString(props?.link),
    internalPageId: readString(props?.internalPageId),
    openInNewTab: readBoolean(props?.openInNewTab) ?? false,
    nofollow: readBoolean(props?.nofollow) ?? false,
    resolvedLink: readString(props?.resolvedLink),
    defaultStyle: defaults?.style,
    defaultIconPlacement: defaults?.iconPlacement,
  });
}

export function buildButtonPropsForPersistence(
  existingProps: Record<string, unknown> | undefined,
  button: ButtonConfig,
): Record<string, unknown> {
  const nextProps = { ...(existingProps || {}) };
  const sanitized = sanitizeButtonConfig(button);

  delete nextProps.resolvedLink;
  delete nextProps.icon;
  delete nextProps.link;
  delete nextProps.internalPageId;
  delete nextProps.openInNewTab;
  delete nextProps.nofollow;
  delete nextProps.linkType;
  delete nextProps.iconPlacement;

  nextProps.text = sanitized.text;
  nextProps.style = sanitized.style || 'primary';
  nextProps.linkType = sanitized.linkType || 'external';

  if (sanitized.icon) {
    nextProps.icon = sanitized.icon;
    nextProps.iconPlacement = sanitized.iconPlacement || 'before';
  }

  if (sanitized.linkType === 'page') {
    if (sanitized.internalPageId) {
      nextProps.internalPageId = sanitized.internalPageId;
    }
    return nextProps;
  }

  if (sanitized.link) {
    nextProps.link = sanitized.link;
  }

  if (sanitized.linkType === 'external') {
    nextProps.openInNewTab = sanitized.openInNewTab === true;
    nextProps.nofollow = sanitized.nofollow === true;
  }

  return nextProps;
}

export function getButtonLinkTypePlaceholder(linkType: ButtonLinkType): string {
  return BUTTON_LINK_TYPE_PLACEHOLDER_MAP.get(linkType) || 'Enter value';
}

export function isExternalLinkType(linkType?: ButtonLinkType): boolean {
  return linkType === 'external';
}

export function isPageLinkType(linkType?: ButtonLinkType): boolean {
  return linkType === 'page';
}

export function isInternalCmsPageButtonLink(
  button: Pick<ButtonConfig, 'linkType' | 'internalPageId'>,
): boolean {
  return button.linkType === 'page' && Boolean(button.internalPageId);
}

export function getButtonHref(
  button: Partial<ButtonConfig>,
  options?: { preview?: boolean },
): string | undefined {
  const linkType = normalizeButtonLinkType(button.linkType, button.internalPageId, button.link);
  const rawLink = readString(button.resolvedLink) || readString(button.link);
  const link = rawLink?.trim();

  if (linkType === 'page') {
    if (options?.preview && button.internalPageId) {
      return cmsPagesPreviewPath({ id: button.internalPageId });
    }

    return link;
  }

  if (!link) {
    return undefined;
  }

  if (linkType === 'phone') {
    return /^tel:/i.test(link) ? link : `tel:${link}`;
  }

  if (linkType === 'email') {
    return /^mailto:/i.test(link) ? link : `mailto:${link}`;
  }

  return normalizeExternalUrl(link);
}

export function getButtonTarget(button: Partial<ButtonConfig>): string | undefined {
  return button.openInNewTab ? '_blank' : undefined;
}

export function getButtonRel(button: Partial<ButtonConfig>): string | undefined {
  const relValues: string[] = [];

  if (button.openInNewTab) {
    relValues.push('noopener', 'noreferrer');
  }

  if (button.nofollow) {
    relValues.push('nofollow');
  }

  return relValues.length > 0 ? Array.from(new Set(relValues)).join(' ') : undefined;
}

export function getButtonIcon(icon?: string): IconDefinition | undefined {
  if (!icon || !BUTTON_ICON_SET.has(icon as ButtonIconName)) {
    return undefined;
  }

  return BUTTON_ICON_MAP.get(icon as ButtonIconName);
}
